import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { createLogger } from './logger';

const logger = createLogger('DeliveryUtils');

/**
 * Get all delivery addresses for a user
 * @param {string} userId - The user's ID
 * @returns {Promise<Array>} Array of delivery objects
 */
export const getUserDeliveries = async (userId) => {
  try {
    const deliveriesQuery = query(
      collection(db, 'Deliveries'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(deliveriesQuery);
    const deliveries = [];

    snapshot.forEach((doc) => {
      deliveries.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return deliveries;
  } catch (error) {
    // Handle permission errors gracefully - this might happen if the Deliveries collection doesn't exist yet
    if (error.code === 'permission-denied') {
      logger.warn(
        'Permission denied accessing Deliveries collection. This is normal if the collection has not been migrated yet.'
      );
      return [];
    }
    logger.error('Error fetching user deliveries:', error);
    return [];
  }
};

/**
 * Add a new delivery address for a user
 * @param {string} userId - The user's ID
 * @param {string} address - The delivery address
 * @param {string} phone - The delivery phone number
 * @param {boolean} isDefault - Whether this is the default address
 * @returns {Promise<string>} The ID of the created delivery document
 */
export const addDeliveryAddress = async (userId, address, phone = '', isDefault = false) => {
  try {
    const deliveryData = {
      userId,
      address: address.trim(),
      phone: phone.trim(),
      isDefault,
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    };

    const docRef = await addDoc(collection(db, 'Deliveries'), deliveryData);
    return docRef.id;
  } catch (error) {
    logger.error('Error adding delivery address:', error);
    throw error;
  }
};

/**
 * Update an existing delivery address
 * @param {string} deliveryId - The delivery document ID
 * @param {Object} updates - The fields to update
 * @returns {Promise<void>}
 */
export const updateDeliveryAddress = async (deliveryId, updates) => {
  try {
    const deliveryRef = doc(db, 'Deliveries', deliveryId);
    await updateDoc(deliveryRef, {
      ...updates,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  } catch (error) {
    logger.error('Error updating delivery address:', error);
    throw error;
  }
};

/**
 * Delete a delivery address
 * @param {string} deliveryId - The delivery document ID
 * @returns {Promise<void>}
 */
export const deleteDeliveryAddress = async (deliveryId) => {
  try {
    const deliveryRef = doc(db, 'Deliveries', deliveryId);
    await deleteDoc(deliveryRef);
  } catch (error) {
    logger.error('Error deleting delivery address:', error);
    throw error;
  }
};

/**
 * Set a delivery address as default
 * @param {string} userId - The user's ID
 * @param {string} deliveryId - The delivery document ID to set as default
 * @returns {Promise<void>}
 */
export const setDefaultDeliveryAddress = async (userId, deliveryId) => {
  try {
    // First, unset all existing defaults for this user
    const deliveriesQuery = query(
      collection(db, 'Deliveries'),
      where('userId', '==', userId),
      where('isDefault', '==', true)
    );

    const snapshot = await getDocs(deliveriesQuery);
    const updatePromises = snapshot.docs.map((doc) =>
      updateDoc(doc.ref, {
        isDefault: false,
        updatedAt: Timestamp.fromDate(new Date()),
      })
    );

    await Promise.all(updatePromises);

    // Then set the new default
    const deliveryRef = doc(db, 'Deliveries', deliveryId);
    await updateDoc(deliveryRef, {
      isDefault: true,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  } catch (error) {
    logger.error('Error setting default delivery address:', error);
    throw error;
  }
};
